import { Form } from '@app/components/Form'
import { AuthLayout } from '@app/components/layout/AuthLayout'
import { TextInput } from '@app/components/inputs/TextInput'
import { nextAuthOptions } from '@app/lib/server/nextAuthOptions'
import { Button, Text, createStyles, Group } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { IconSend } from '@tabler/icons-react'
import { type GetServerSidePropsContext, type GetServerSidePropsResult, type NextPage } from 'next'
import { useRouter } from 'next/router'
import { type Session } from 'next-auth'
import { getServerSession } from 'next-auth/next'
import { signIn } from 'next-auth/react'
import { useCallback, useState } from 'react'
import { z } from 'zod'
import Link from 'next/link'
import { graphql, useMutation } from 'react-relay'
import { showNotification } from '@mantine/notifications'
import { IconAlertCircle } from '@tabler/icons-react'

const useStyles = createStyles((theme) => ({
  wrapper: {
    width: '50%'
  },
  form: {
    width: '100%'
  },
  button: {
    backgroundColor: theme.colors.brandPrimary[0],
    '&:hover': {
      backgroundColor: theme.colors.brandPrimary[0]
    }
  },
  link: {
    color: theme.colors.brandSecondary[0],
    textDecoration: 'none',
    fontSize: '0.875rem'
  }
}))

const schema = z.object({
  email: z.string().trim().email()
})

const isUserAlreadyExistsMutation = graphql`
  mutation requestLinkIsUserAlreadyExistsMutation($input: IsUserAlreadyExistsInput!) {
    isUserAlreadyExists(input: $input) {
      boolean
      clientMutationId
    }
  }
`

interface RequestLinkPageQueryParams {
  callbackUrl?: string
  isSignup?: string
}

const RequestLinkPage: NextPage = () => {
  const router = useRouter()
  const { classes } = useStyles()
  const { callbackUrl = '/', isSignup = 'false' }: RequestLinkPageQueryParams = router.query
  const [isRequestingLink, setIsRequestingLink] = useState(false)
  const form = useForm<{
    email: string
  }>({
    initialValues: {
      email: ''
    },
    validate: zodResolver(schema)
  })

  const [isUserAlreadyExists, isUserExistsLoading] = useMutation<any>(isUserAlreadyExistsMutation)

  const signInAndRedirect = useCallback(
    async (email: string) => {
      // the url that this method returns strips out any useful params, so we will build our own below
      await signIn('email', {
        callbackUrl,
        email,
        redirect: false
      })

      await router.push(`/auth/verify-request?${new URLSearchParams({ callbackUrl, email })}`)
    },
    [callbackUrl, router]
  )

  return (
    <AuthLayout title='Request Code'>
      <Group
        position='center'
        className={classes.wrapper}
      >
        <Text
          color='dimmed'
          mb='lg'
        >
          {`Enter your email below to be sent code to ${isSignup === 'true' ? 'signup' : 'login'} with.`}
        </Text>
        <Form
          className={classes.form}
          buttons={
            <Button
              color='blue'
              disabled={isRequestingLink || !form.isValid()}
              fullWidth
              leftIcon={<IconSend size={18} />}
              type='submit'
              className={classes.button}
            >
              Send Code
            </Button>
          }
          isSaving={isRequestingLink}
          isValid={form.isValid()}
          onSubmit={form.onSubmit(
            useCallback(
              async ({ email }) => {
                setIsRequestingLink(true)
                if (isSignup === 'true') {
                  isUserAlreadyExists({
                    variables: {
                      input: {
                        _email: email
                      }
                    },
                    onCompleted: (response) => {
                      if (response?.isUserAlreadyExists?.boolean) {
                        showNotification({
                          color: 'red',
                          icon: <IconAlertCircle size={18} />,
                          title: 'Error',
                          message: 'User Already exists with this email'
                        })
                        setIsRequestingLink(false)
                      } else {
                        signInAndRedirect(email)
                      }
                    }
                  })
                } else {
                  isUserAlreadyExists({
                    variables: {
                      input: {
                        _email: email
                      }
                    },
                    onCompleted: (response) => {
                      if (!response?.isUserAlreadyExists?.boolean) {
                        showNotification({
                          color: 'red',
                          icon: <IconAlertCircle size={18} />,
                          title: 'Error',
                          message: `User doesn't exist with this email`
                        })
                        setIsRequestingLink(false)
                      } else {
                        signInAndRedirect(email)
                      }
                    }
                  })
                }
              },
              [isSignup, isUserAlreadyExists, signInAndRedirect]
            )
          )}
        >
          <TextInput
            autoComplete='email'
            disabled={isRequestingLink}
            icon={null}
            mb='lg'
            placeholder='Enter Your Email'
            label='Email'
            required
            type='email'
            {...form.getInputProps('email')}
          />
        </Form>
        <Group
          w='100%'
          position='center'
          spacing='xs'
        >
          <Text
            size='sm'
            color='dimmed'
          >
            Something went wrong?
          </Text>
          <Link
            href={`${isSignup === 'true' ? '/auth/signup' : '/auth/login'}`}
            className={classes.link}
          >
            Back
          </Link>
        </Group>
      </Group>
    </AuthLayout>
  )
}

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<{ session: Session }>> => {
  const session = await getServerSession(ctx.req, ctx.res, nextAuthOptions)

  if (session) {
    return {
      redirect: {
        destination: ctx.req.headers.referer || '/',
        permanent: false
      }
    }
  }

  return {
    props: {
      session
    }
  }
}

// noinspection JSUnusedGlobalSymbols
export default RequestLinkPage
